import React, { useState, useEffect } from 'react';

import { useParams } from 'react-router-dom';
import { EmptyDress, IDress, IProject } from 'utils/types/storyTypes';
import TableToolbar from 'components/table/TableToolbar';
import MainTable from 'components/table/MainTable';
import DefaultButton from 'components/button/DefaultButton';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  showApiErrorMessage,
  showApiSuccessMessage,
} from 'utils/helper/message';
import ActionButton from 'components/table/ActionButton';
import { Edit2, Trash } from 'iconsax-react';
import SelectMultiple from '../_component/SearchMultiple';

import DeleteModal from 'components/modal/DeleteModal';
import CostumeCreateModal from './CostumeCreateModal';
import CostumeUpdateModal from './CostumeUpdateModal';
import { queryProjectCodeList } from 'apis/common/commonApis';
import { costumeApis } from 'apis/resource/costumeApis';
import CheckboxStyled from 'components/CheckboxStyled';

/**
 * * 리소스 - 의상목록
 * 2023.12 : HJ 수정보완
 */

interface IProps {
  currentProject: IProject;
}

const CostumeTable = ({ currentProject }: IProps) => {
  const { t } = useTranslation();
  const { projectId } = useParams(); // id: 프로젝트 id
  const [searchData, setSearchData] = useState<string[]>([]); // 검색어
  const [visible, setVisible] = useState({
    register: false,
    delete: false,
    update: false,
    name: '',
  }); // 모달 show on/off

  // 컨트롤할 의상 선택
  const [dressData, setDressData] = useState<IDress>(EmptyDress);

  const { isLoading: isLoadingModelList, data: dropdownModelList = [] } =
    queryProjectCodeList(projectId, 'model'); // 모델 목록
  const { isLoading: isLoadingProfileList, data: dropdownProfileList = [] } =
    queryProjectCodeList(projectId, 'character'); // 프로필 캐릭터

  const [refresh, setRefresh] = useState<boolean>(false);

  // 의상 목록
  const [listData, setListData] = useState<IDress[]>([]); // 서버에서 받아오는 의상 목록
  const [tableData, setTableData] = useState<IDress[]>([]); // 테이블 소스(필터링, 등등 적용된)
  const [loading, setLoading] = useState<boolean>(false);

  const requestDressList = async () => {
    if (projectId) {
      try {
        const resultList = await costumeApis.costumeList(projectId);
        setListData(resultList);

        // console.log(`costume modelList : `, dropdownModelList);
        // console.log(`costume profileList : `, dropdownProfileList);
      } catch (err) {
        console.log('fetch data error: ', err);
      }
    }
  };

  useEffect(() => {
    if (listData) {
      setTableData(listData);
    }
  }, [listData]);

  useEffect(() => {
    requestDressList();
  }, [refresh]);

  // 배경 삭제
  const handleClickDeleteModal = (record) => {
    setDressData(record);
    setVisible({
      ...visible,
      delete: true,
      name: record?.live_name,
    });
  }; // ?

  const handleDelete = async () => {
    if (dressData) {
      try {
        setLoading(true);
        await costumeApis.costumeDelete(projectId, dressData.dress_id);
        setRefresh(!refresh);
        setLoading(false);
        setVisible({
          ...visible,
          delete: false,
          name: dressData?.dress_name,
        });

        showApiSuccessMessage('선택한 의상이 삭제되었습니다');
      } catch (err) {
        setLoading(false);
        showApiErrorMessage(err);
      }
    }
  };

  // 버튼 메뉴
  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <Edit2
            size='24'
            onClick={() => {
              setDressData(record);
              setVisible({ ...visible, update: true });
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 2,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record);
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'dress_id',
      width: 'fit-content',
      align: 'center',
      // sorter: (a, b) => a.dress_id - b.dress_id,
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.costumeContent.table.character',
      ),
      dataIndex: 'speaker',
      width: '7rem',
      sorter: (a, b) => {
        if (a.speaker && b.speaker) {
          return a.speaker.localeCompare(b.speaker, undefined, {
            sensitivity: 'base',
          });
        } else if (a.speaker && !b.speaker) {
          return -1;
        } else if (!a.speaker && b.speaker) {
          return 1;
        } else {
          return 0;
        }
      },
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.costumeContent.table.costumeName',
      ),
      dataIndex: 'dress_name',
      width: '9rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.costumeContent.table.model',
      ),
      dataIndex: 'model_name',
      width: '7rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.costumeContent.table.default',
      ),
      dataIndex: 'is_default',
      width: '7rem',
      render: (_) => {
        return <CheckboxStyled isChecked={_} />;
      },
    },
    {
      title: '',
      dataIndex: '',
      width: '4rem',
      render: (text, record) => {
        const menus = [tableActionMenu(record)];
        return <ActionButton menus={menus} />;
      },
    },
  ];

  // handle search input onChange
  const handleSelectChange = (value: any, data: any) => {
    setSearchData(value);
  };

  useEffect(() => {
    const cloneData = structuredClone(listData);

    if (cloneData) {
      if (searchData.length) {
        const newArray = cloneData.filter((obj) =>
          searchData.includes(obj.speaker),
        );
        setTableData(newArray);
      } else {
        setTableData(listData);
      }
    }
  }, [searchData]);

  if (isLoadingModelList || isLoadingProfileList) return <>Loading Data...</>;

  return (
    <>
      <TableToolbar>
        <SelectMultiple
          optionList={[]}
          // optionList={dropdownProfileList?.map((e) => {
          //   return {
          //     value: e.code,
          //     label: e.code_name,
          //   };
          // })}
          placeholder='Search...'
          mode='tags'
          onChange={handleSelectChange}
          width='320px'
          value={searchData}
          setValue={setSearchData}
        />

        <div>
          <DefaultButton
            name='register'
            onClick={() => setVisible({ ...visible, register: true })}
          >
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.costumeContent.header.create',
            )}
          </DefaultButton>
        </div>
      </TableToolbar>

      <TableWrapper>
        <MainTable rowKey='id' columns={columnsOrigin} dataSource={tableData} />
      </TableWrapper>

      <CostumeCreateModal
        visible={visible}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        projectId={projectId}
        profileList={dropdownProfileList}
      />

      <CostumeUpdateModal
        visible={visible}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        projectId={projectId}
        data={dressData}
        setData={setDressData}
        charactersData={dropdownProfileList}
        modelsData={dropdownModelList}
      />

      <DeleteModal
        visible={visible.delete}
        setVisible={(e) => {
          setVisible({ ...visible, delete: e });
        }}
        title={t(
          'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.costumeContent.deleteModal.title',
        )}
        content={t(
          'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.costumeContent.deleteModal.content',
        )}
        label={visible.name}
        handleDelete={handleDelete}
        loading={loading}
      />
    </>
  );
};

export default CostumeTable;

const TableWrapper = styled.div`
  .ant-table table tr {
    vertical-align: top;
  }
  .ant-table-thead > tr > th {
    padding: 0.8rem 0.8rem;
  }
  .ant-table-thead > tr > th,
  .ant-table-column-title {
    font-size: ${theme.fontSizes.normal};
    color: ${theme.colors.dark_300} !important;
    font-weight: ${theme.fontWeights.bold};
    line-height: 16px;
    letter-spacing: 0.006rem;
  }
  .ant-table-wrapper
    .ant-table-tbody
    > tr
    > td
    > .ant-table-wrapper:only-child
    .ant-table {
    margin: 0 10px;
  }
`;

const IconArrowWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 5px;
  p {
    font-size: ${theme.fontSizes.normal} !important;
    font-weight: ${theme.fontWeights.bold} !important;
    line-height: 24px !important;
    letter-spacing: 0.2px !important;
    color: ${theme.colors.blackContent} !important;
  }
`;
